import { AppTimestampContext } from "global/cnr/contexts/AppTimestampContext";
import { TimestampsContext } from "global/cnr/contexts/TimestampsContext";
import { shouldUseCache } from "global/cnr/contexts/useHelpers/useHelpers";
import { createRefPath_event } from "global/firestoreData/appData/appRefPaths";
import _ from 'lodash';
import { dataItems_sportsSeasonResults } from "projectSpecific/sports/cnr/reducers/SportsSeasonReducer";
import { useCallback, useContext } from "react";
import { DatabaseContext } from "../../../global/cnr/contexts/DatabaseContext";
import { _showAppLogs } from "../../../global/cnr/contexts/LogContext";
import { ParentContext } from "../../../global/cnr/contexts/ParentContext";
import { useCacheDataManager } from "../../../global/cnr/contexts/useHelpers/cacheDataManager";
import { useMultiDoc } from "../../../global/cnr/contexts/useHelpers/useMultiDoc";
import { _cacheGroupTypes } from "../../../global/cnr/contexts/useHelpers/useTimestamp";
import { seasonalHelpers } from "../cnr/reducers/reducerHelpers/seasonalHelpers";
import { _sportsInfoTypes } from "./useSportsInfo";

export const _matchSeasonalCollection = 'matches_seasonal'

const _applyCache = true

export const useSportResults = ({ sportsInfoType }) => {

  // const cache_groupType = sportsInfoType + 'Info';
  const cache_groupType = _cacheGroupTypes.sportsSeasonResultsInfo;

  // parentContext
  const parentContext = useContext(ParentContext)
  const { states } = parentContext ?? {}
  const { paps_state } = states ?? {}
  const { pathViews } = paps_state ?? {}
  const { sports: sportsKey } = pathViews ?? {}

  // appTimestampContext 
  const appTimestampContext = useContext(AppTimestampContext);
  const { appTimestamp_state } = appTimestampContext ?? {}
  const { timestampInfo } = appTimestamp_state ?? {}
  const { timestamps } = timestampInfo ?? {}

  // timestampsContext
  const timestampsContext = useContext(TimestampsContext)
  const { timestamps_state } = timestampsContext ?? {}
  const { _validatedTimestamps } = timestamps_state ?? {}

  // databaseContext
  const databaseContext = useContext(DatabaseContext);
  const { database_fns } = databaseContext ?? {};

  const { handleGet_multiDocs, handleGet_multiDocs_noListen } = useMultiDoc()
  const cdm = useCacheDataManager()

  const handle_sportsResultsInfo = async ({ sportsResultsData, pdks, callback, fromCache, newTimestamp }) => {
    const sportsSeason_datas = seasonalHelpers.ammend_sportsSeasonDataz(sportsResultsData, '2026')
    sportsSeason_datas.parentDataKeys = pdks
    sportsSeason_datas._validatedTimestamp = newTimestamp
    _showAppLogs.sportInfo && console.log(`✅${fromCache ? '🗄️' : '🔥'}${sportsInfoType}`, sportsSeason_datas)
    if (!fromCache) {
      const fullData = { sportsResultsData }
      await cdm.mergeToGroup(cache_groupType, pathViews.sports, fullData, newTimestamp, true);
    }
    callback(sportsSeason_datas)
  }

  /**
   * handles the returned data from _refPath_s
   * @param {object} sport 
   */
  const handleDbData_sportsSeasonResults = (sport, cb_main, ts) => {
    const cb = async (sportsResultsData) => handle_sportsResultsInfo({ sportsResultsData, callback: cb_main, fromCache: false, newTimestamp: ts })
    const { latestSeason } = sport ?? {}
    const _pks = seasonalHelpers.getPks(pathViews, latestSeason, sportsKey, ['teams'])
    handleGet_multiDocs_noListen({ key: 'sportsResults', sportsDocs: dataItems_sportsSeasonResults, pathViews, pks: _pks, callback: cb })
  }

  const getSportsResults = useCallback(async (_sportsInfoType, callback, ts_ssri) => {

    const cgt = _sportsInfoType + 'Info';
    const currentTimestamp_db = ts_ssri // timestamps?.[cgt]?.[pathViews.sports];

    const cb_data = (data) => {
      switch (_sportsInfoType) {
        case _sportsInfoTypes.sportsSeasonResults:
          handleDbData_sportsSeasonResults(data, callback, currentTimestamp_db)
      }
    }

    const cb_cache = (cachedData) => {
      // fromCache and newTimestamp included in cachedData
      switch (_sportsInfoType) {
        case _sportsInfoTypes.sportsSeasonResults:
          handle_sportsResultsInfo({ ...cachedData, callback, fromCache: true })
      }
    }

    switch (_sportsInfoType) {
      case _sportsInfoTypes.sportsSeasonResults:
        getItemData(cdm, currentTimestamp_db, cgt, pathViews, pathViews.sports, 'sports', true, database_fns, cb_cache, cb_data, _validatedTimestamps)
    }
  }, [])

  return { getSportsResults }

};

const getItemData = async (
  cdm,
  currentTimestamp_db,
  cgt,
  pathViews,
  pathKey,
  collectionName,
  ignoreId,
  database_fns,
  callback_cache,
  callback_data,
  _validatedTimestamps
) => {

  // get the cache
  const cache_result = await cdm.getSubItem(cgt, pathKey);

  const { _timestamp: timestamp_cache } = cache_result ?? {}

  // KEY GUARD: already have this version 
  const validatedTimestamp = _validatedTimestamps?.[cgt] ?? {};

  if (validatedTimestamp && validatedTimestamp === currentTimestamp_db) {
    _showAppLogs.sportInfo && console.log(`✅${cgt}`)
    // return; // 🚫 do nothing
  }

  const allowCache = shouldUseCache(timestamp_cache, currentTimestamp_db, cgt)

  // return the cache data if available
  if (_applyCache && !_.isEmpty(cache_result) && allowCache) {
    callback_cache({ ...cache_result, fromCache: true, newTimestamp: currentTimestamp_db })
    return;
  }

  // console.group('📦getItemData', cgt)
  // console.log('currentTimestamp_db', timestamp_cache, currentTimestamp_db, currentTimestamp_db > timestamp_cache)
  // console.log('cache_result', cache_result)
  // console.log('allowCache', allowCache)
  // console.groupEnd()

  // get the data from the database
  const _refPath = createRefPath_event(pathViews, [collectionName, pathKey])
  // _showAppLogs.sportInfo && console.log(`🔥➡️ Fetching doc ${sportsInfoType}/${cacheType} from database (${_refPath})`);
  database_fns.get_doc({
    refPath: _refPath,
    opts: { ignoreId: ignoreId },
    callback: callback_data
  })
} 